// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * promoteTrustedPerson - V41
 *
 * Promoveert reserve-VP naar primair en degradeert huidige primair naar reserve.
 * Staat in een apart bestand zodat services/trustedPerson/index.ts (beveiligd) onaangetast blijft.
 *
 * Garantie: er zijn nooit twee gelijktijdige PRIMARY-VP's.
 */
import { db }               from '@/lib/db'
import { nowISO }           from '@/lib/utils'
import {
  TrustedPersonRole,
  ConfirmationStatus,
  SyncStatus,
} from '@/types'

export interface PromoteResult {
  success: boolean
  message: string
}

export async function promoteTrustedPerson(
  reserveId: string,
  userId:    string,
): Promise<PromoteResult> {

  // Haal alle VP-records op voor deze gebruiker
  const all = await db.trusted_persons
    .where('userId')
    .equals(userId)
    .toArray()

  const reserve = all.find(p => p.id === reserveId)
  if (!reserve) {
    return { success: false, message: 'Tweede vertrouwenspersoon niet gevonden.' }
  }
  if (reserve.confirmationStatus === ConfirmationStatus.REVOKED) {
    return { success: false, message: 'Deze vertrouwenspersoon is ingetrokken en kan niet worden gepromoveerd.' }
  }
  if (reserve.role === TrustedPersonRole.PRIMARY) {
    return { success: false, message: `${reserve.firstName} is al primaire vertrouwenspersoon.` }
  }

  // Vind huidige actieve primaire VP (mag er maar één zijn)
  const currentPrimary = all.find(
    p => p.role === TrustedPersonRole.PRIMARY &&
         p.confirmationStatus !== ConfirmationStatus.REVOKED &&
         p.id !== reserveId
  )

  const now = nowISO()

  // Stap 1 - degradeer huidige primair naar reserve (als aanwezig)
  if (currentPrimary) {
    await db.trusted_persons.update(currentPrimary.id, {
      role:       TrustedPersonRole.RESERVE,
      updatedAt:  now,
      syncStatus: SyncStatus.PENDING,
    })
    if (currentPrimary.accessKeyId) {
      await db.access_keys.update(currentPrimary.accessKeyId, {
        keyType:    TrustedPersonRole.RESERVE,
        syncStatus: SyncStatus.PENDING,
      })
    }
  }

  // Stap 2 - promoveer reserve naar primair
  await db.trusted_persons.update(reserveId, {
    role:             TrustedPersonRole.PRIMARY,
    lastConfirmedAt:  now,   // optioneel veld uit V41 - geen schema-upgrade
    updatedAt:        now,
    syncStatus:       SyncStatus.PENDING,
  })
  if (reserve.accessKeyId) {
    await db.access_keys.update(reserve.accessKeyId, {
      keyType:    TrustedPersonRole.PRIMARY,
      syncStatus: SyncStatus.PENDING,
    })
  }

  // Stap 3 - veiligheidscheck: nooit meer dan één PRIMARY
  const afterAll = await db.trusted_persons
    .where('userId')
    .equals(userId)
    .toArray()

  const activePrimaries = afterAll.filter(
    p => p.role === TrustedPersonRole.PRIMARY &&
         p.confirmationStatus !== ConfirmationStatus.REVOKED
  )

  if (activePrimaries.length > 1) {
    // Noodrem: degradeer alle extras behalve de nieuwe primair
    for (const extra of activePrimaries) {
      if (extra.id !== reserveId) {
        await db.trusted_persons.update(extra.id, {
          role:       TrustedPersonRole.RESERVE,
          updatedAt:  now,
          syncStatus: SyncStatus.PENDING,
        })
      }
    }
    return {
      success: false,
      message: 'Dubbele primaire VP gedetecteerd en automatisch hersteld. Controleer de instellingen.',
    }
  }

  return {
    success: true,
    message: `${reserve.firstName} is nu uw primaire vertrouwenspersoon.`,
  }
}
